import frameSvgTemplate from './assets/frame.svg?raw';
import { getLogger } from './logger';
import type { WidgetPosition, WidgetTranslations } from './types';

const logger = getLogger('UI');

/**
 * Check browser SVG support
 */
export function checkSvgSupport(): boolean {
  return !!(
    typeof SVGRect !== 'undefined' &&
    document.createElementNS &&
    document.createElementNS('http://www.w3.org/2000/svg', 'svg').createSVGRect
  );
}

/**
 * Process SVG template and replace placeholders with translations
 */
export function processSvgTemplate(translations: WidgetTranslations): string {
  let svgContent = frameSvgTemplate;

  // Escape special characters to prevent SVG injection
  const escapeSvgText = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Replace placeholders with translation values
  svgContent = svgContent.replace(/{{title}}/g, escapeSvgText(translations.assistantName));
  svgContent = svgContent.replace(
    /{{description}}/g,
    escapeSvgText(translations.description || '')
  );

  // Add browser-specific attributes if needed
  const isIE = /MSIE|Trident/.test(navigator.userAgent);
  if (isIE) {
    // Add IE-specific SVG fixes
    svgContent = svgContent.replace('<svg ', '<svg role="img" ');
  }

  return svgContent;
}

/**
 * Default translations for the widget
 */
export const defaultTranslations: WidgetTranslations = {
  assistantName: 'AI Assistant',
  connecting: 'Connecting',
  error: 'An error occurred',
  streaming: 'Listening',
  stop: 'Stop',
  ended: 'Conversation ended',
  description: 'Hello! I am your AI assistant. How can I help you today?',
};

/**
 * Get translations from window.SirmaAI.translations with type safety
 * Returns default translations merged with any provided translations
 */
export function getTranslations(): WidgetTranslations {
  logger.debug('Getting translations');
  const customTranslations = window?.SirmaAI?.translations;

  if (!customTranslations) {
    logger.debug('No custom translations found, using defaults');
    return defaultTranslations;
  }

  // Merge custom translations over defaults
  const mergedTranslations = { ...defaultTranslations, ...customTranslations };
  const customKeys = Object.keys(customTranslations);

  logger.debug('Merged translations', {
    customKeys,
    hasCustomTranslations: customKeys.length > 0,
  });

  return mergedTranslations;
}

/**
 * Get widget position from window.SirmaAI.config.position with type safety
 * Returns 'center' as default position when not specified or invalid
 */
export function getWidgetPosition(): WidgetPosition {
  logger.debug('Getting widget position');
  const configuredPosition = window?.SirmaAI?.config?.position;

  if (!configuredPosition) {
    logger.debug('No position configured, using default: center');
    return 'center';
  }

  // Validate that the configured position is one of the allowed values
  const validPositions: WidgetPosition[] = [
    'top-left',
    'top-center',
    'top-right',
    'center-left',
    'center',
    'center-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ];

  if (validPositions.includes(configuredPosition)) {
    logger.debug('Using configured position', { position: configuredPosition });
    return configuredPosition;
  } else {
    logger.warn('Invalid position configured, using default: center', {
      invalidPosition: configuredPosition,
      validPositions,
    });
    return 'center';
  }
}
